import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2 } from 'lucide-react';
import { formatIls, giftAdmin, type GiftTransaction } from '@/lib/giftCards';
import { newIdempotencyKey, remainingReversible, validateCredit } from '@/lib/giftValidation';

interface Props {
  cardId: string;
  currency: string;
  transactions: GiftTransaction[];
  onDone: () => void | Promise<void>;
}

/**
 * Corrections are always tied to one succeeded debit — there is no arbitrary
 * balance overwrite anywhere in the system.
 */
export function GiftCreditForm({ cardId, currency, transactions, onDone }: Props) {
  const [debitId, setDebitId] = useState('');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [idemKey, setIdemKey] = useState(() => newIdempotencyKey('credit'));

  const reversible = useMemo(
    () =>
      transactions
        .filter((t) => t.type === 'debit' && t.status === 'succeeded')
        .map((t) => ({ tx: t, remaining: remainingReversible(t.id, transactions as never) }))
        .filter((r) => r.remaining > 0),
    [transactions],
  );

  const selectedRemaining = debitId ? remainingReversible(debitId, transactions as never) : 0;
  const check = useMemo(
    () => validateCredit(debitId, amount, reason, transactions as never, cardId),
    [debitId, amount, reason, transactions, cardId],
  );

  const submit = async () => {
    if (!check.ok || busy) return;
    setBusy(true);
    setError(null);
    try {
      await giftAdmin('transaction.credit', {
        gift_card_id: cardId,
        amount: Number(amount),
        currency,
        reversed_transaction_id: debitId,
        reason: reason.trim(),
        idempotency_key: idemKey,
      });
      setDebitId('');
      setAmount('');
      setReason('');
      setConfirming(false);
      setIdemKey(newIdempotencyKey('credit'));
      await onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'ההחזרה נכשלה');
    } finally {
      setBusy(false);
    }
  };

  if (!reversible.length) {
    return (
      <div className="rounded-lg border p-4 text-sm text-muted-foreground">
        אין חיובים שניתן להחזיר בשובר זה.
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-lg border p-4">
      <div>
        <h4 className="font-medium">החזרה / תיקון</h4>
        <p className="text-xs text-muted-foreground">
          ההחזרה מתבצעת מול חיוב קיים בלבד, עד לסכום שנותר להחזרה.
        </p>
      </div>

      <div className="space-y-1">
        <Label htmlFor="credit-debit">חיוב להחזרה</Label>
        <select
          id="credit-debit"
          className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
          value={debitId}
          onChange={(e) => {
            setDebitId(e.target.value);
            setConfirming(false);
          }}
          disabled={busy}
        >
          <option value="">בחר/י חיוב…</option>
          {reversible.map(({ tx, remaining }) => (
            <option key={tx.id} value={tx.id}>
              {new Date(tx.created_at).toLocaleDateString('he-IL')} · {formatIls(tx.amount)} · נותר להחזרה{' '}
              {formatIls(remaining)}
            </option>
          ))}
        </select>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="credit-amount">סכום ההחזרה (₪)</Label>
          <Input
            id="credit-amount"
            type="number"
            min="0"
            step="1"
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value);
              setConfirming(false);
            }}
            disabled={busy || !debitId}
          />
          {debitId && (
            <p className="text-xs text-muted-foreground">נותר להחזרה: {formatIls(selectedRemaining)}</p>
          )}
        </div>
        <div className="space-y-1">
          <Label htmlFor="credit-reason">סיבה</Label>
          <Textarea
            id="credit-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            disabled={busy}
          />
        </div>
      </div>

      {debitId && amount && !check.ok && <p className="text-sm text-destructive">{check.error}</p>}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {confirming && check.ok ? (
        <div className="space-y-2 rounded-md bg-muted p-3 text-sm">
          <p>
            החזרה של <strong>{formatIls(Number(amount))}</strong>. יישאר להחזרה:{' '}
            <strong>{formatIls(check.remainingAfter)}</strong>
          </p>
          <div className="flex gap-2">
            <Button size="sm" onClick={submit} disabled={busy}>
              {busy && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
              אישור ההחזרה
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setConfirming(false)} disabled={busy}>
              ביטול
            </Button>
          </div>
        </div>
      ) : (
        <Button size="sm" onClick={() => setConfirming(true)} disabled={!check.ok || busy}>
          המשך לאישור
        </Button>
      )}
    </div>
  );
}
